import { useEffect } from 'react';

import Highcharts from '@/components/chart/highcharts';
import { Z_INDEX } from '@/constants';

type SeriesWithPulseAnimation = Highcharts.Series & {
  pulseShadow?: Highcharts.SVGElement;
  pulseMain?: Highcharts.SVGElement;
  markerGroup?: Highcharts.SVGElement;
};

export const useAnimateLiveChart = () => {
  useEffect(() => {
    const addPointHandler = function (
      this: Highcharts.Series,
      // Matches Highcharts.EventCallbackFunction second parameter (Dictionary is typed with any upstream).
      eventArguments?: Event | Highcharts.Dictionary<Highcharts.Point | undefined>
    ) {
      const raw = eventArguments && 'point' in eventArguments ? eventArguments.point : undefined;
      const point = raw instanceof Highcharts.Point ? raw : undefined;
      if (!point) return;

      const series = this as SeriesWithPulseAnimation;
      const chart = series.chart;

      const x = series.xAxis.toPixels(point.x, true);
      const y = series.yAxis.toPixels(point.y ?? 0, true);

      if (!series.pulseShadow) {
        series.pulseShadow = chart.renderer
          .circle(x, y, 6)
          .attr({
            fill: series.color,
            opacity: 0.8,
            zIndex: Z_INDEX.base.chart,
          })
          .add(series.markerGroup);
      }

      if (!series.pulseMain) {
        series.pulseMain = chart.renderer
          .circle(x, y, 6)
          .attr({
            fill: series.color,
            stroke: 'white',
            'stroke-width': 2,
            zIndex: Z_INDEX.base.chartSeries,
          })
          .add(series.markerGroup);
      }

      const pulseShadow = series.pulseShadow;
      const pulseMain = series.pulseMain;
      if (!pulseShadow || !pulseMain) return;

      pulseShadow.attr({ x, y, r: 6 });
      pulseMain.attr({ x, y, r: 6 });

      let pulseCount = 0;
      const maxPulses = 10;

      const animateGlow = () => {
        if (pulseCount >= maxPulses) {
          pulseShadow.attr({ opacity: 0 });
          return;
        }

        pulseCount++;
        pulseShadow.attr({ r: 6, opacity: 0.8 }).animate(
          {
            r: 18,
            opacity: 0,
          },
          {
            duration: 3000,
            easing: 'easeOutCubic',
            complete: () => {
              setTimeout(animateGlow, 500);
            },
          }
        );
      };

      setTimeout(() => {
        animateGlow();
      }, 1);
    } satisfies Highcharts.EventCallbackFunction<Highcharts.Series>;

    Highcharts.addEvent(Highcharts.Series, 'addPoint', addPointHandler);

    return () => {
      // ✅ Clean up: remove event listener
      Highcharts.removeEvent(Highcharts.Series, 'addPoint', addPointHandler);
    };
  }, []);
};
